/**
 * dlcPromptBuilder.js
 *
 * Builds structured agent prompts for DLC implementations.
 * The generated prompt is sent via the `populate-chat-input` CustomEvent
 * to activate the chat agent with full context about the requirement.
 */

/**
 * Build a prompt that instructs an AI agent to implement a requirement.
 *
 * @param {Object} req - Parsed requirement object
 * @param {Object} agentConfig - { agentName, swiId, swiName, designMd }
 * @returns {string} Prompt string ready to send to the chat
 */
export function buildImplementationPrompt(req, agentConfig = {}) {
  const { agentName = '', swiId = '', swiName = '', designMd = '' } = agentConfig;

  const lines = [
    `[DLC IMPLEMENTATION REQUEST]`,
    ``,
    `## Requirement`,
    `- **ID:** ${req.id || 'N/A'}`,
    `- **Title:** ${req.title}`,
    req.priority ? `- **Priority:** ${req.priority}` : null,
    req.type ? `- **Type:** ${req.type}` : null,
    req.category ? `- **Category:** ${req.category}` : null,
    req.description ? `- **Description:** ${req.description}` : null,
    ``,
    `## Implementation Context`,
    swiId ? `- **Software Item (SWI):** ${swiId}${swiName ? ` — ${swiName}` : ''}` : null,
    agentName ? `- **Assigned Agent Role:** ${agentName}` : null,
    ``,
  ].filter(l => l !== null);

  if (designMd && designMd.trim()) {
    lines.push(`## Design Document`);
    lines.push(``);
    lines.push(designMd.trim());
    lines.push(``);
  }

  lines.push(`## Instructions`);
  lines.push(`Please implement this requirement according to the design document and project conventions.`);
  lines.push(`Follow the existing code patterns in this project and ensure tests are written for the new functionality.`);
  lines.push(`When creating or modifying files, use unified diff format.`);

  return lines.join('\n');
}

/**
 * Dispatch the implementation prompt to the chat input.
 * Uses the existing `populate-chat-input` CustomEvent already handled in App.jsx.
 *
 * @param {string} prompt
 * @param {boolean} [autoSend=true]
 */
export function sendImplementationPrompt(prompt, autoSend = true) {
  window.dispatchEvent(new CustomEvent('populate-chat-input', {
    detail: { message: prompt, autoSend },
  }));
}
